import { Type } from "@themes/pollinator/ui/Type";
import { Button } from "@themes/pollinator/ui/Button";
import { PollinatorLogo } from "@themes/pollinator/ui/PollinatorLogo";
import { marketingType } from "../../marketing.type";

export type Props = {
  /** Who this form is for, above the title */
  audienceLabel?: string;
  /** The large title beside the form */
  headline?: string;
  /** Paragraphs beside the title */
  introBody?: string[];
  /** The line above the steps, in the form panel */
  stepIntro?: string;
  /** Which step of how many, when the form has more than one */
  stepNumber?: number;
  stepCount?: number;
  /** The wording on the button that advances, e.g. "Continue" or "Book Meeting" */
  primaryLabel?: string;
  /** Hidden when there is nothing to go back to */
  onBack?: () => void;
  /**
   * Where Cancel leads. Cancel takes the place of Back on a screen with
   * nothing to go back to, so a visitor always has a way out of the form.
   */
  cancelHref?: string;
  /** Returns from the first form screen to the intro screen, on a phone */
  onMobileBack?: () => void;
  /** Where the logo leads */
  homeHref?: string;
  onPrimary?: () => void;
  isPrimaryDisabled?: boolean;
  /**
   * Leaves the primary button out while keeping the rest of the footer, for a
   * screen where there is nothing it could do.
   */
  isPrimaryHidden?: boolean;
  isSubmitting?: boolean;
  /** A second way forward under the primary button, e.g. signing in with Google */
  secondaryAction?: React.ReactNode;
  /**
   * What a phone shows. The design gives a phone the argument as a screen of
   * its own and then the form without it, so below the two-panel width this is
   * one or the other; "stacked" keeps both on one page, argument first.
   */
  mobileView?: "stacked" | "intro" | "form";
  /** Leaves the intro screen for the form, on a phone */
  onMobileContinue?: () => void;
  /** The step itself */
  children: React.ReactNode;
  className?: string;
};

/** An email address inside a sentence, which the design draws as a link. */
const EMAIL = /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/;

function withEmailLinks(text: string) {
  return text.split(EMAIL).map((part, i) =>
    EMAIL.test(part) ? (
      <a
        key={i}
        href={`mailto:${part}`}
        // One unbroken line, as drawn: not lifted around the descenders.
        className="underline decoration-[1.5px] underline-offset-[5px] [text-decoration-skip-ink:none]"
      >
        {part}
      </a>
    ) : (
      part
    )
  );
}

/**
 * The accent under the audience label. The intake screens draw it a pixel
 * heavier than the rule under a section label on the homepage.
 */
const EYEBROW_ACCENT = "border-b-[3px] border-brand-600 pb-px";

/**
 * The bar a phone gets across the top: 72px including its rule, with the logo
 * 32px in and 24px down.
 */
const MOBILE_BAR =
  "box-border flex h-[72px] shrink-0 items-start border-b border-brand-700 px-8 pt-6 max-lg:[@media(max-height:740px)]:h-14 max-lg:[@media(max-height:740px)]:pt-4";

/**
 * The design draws a footer's rule inside its 32px of padding rather than on
 * top of it, so the padding above the button gives up the pixel the rule takes.
 * On a short phone screen (740px or less) it closes up, as the bar above does.
 */
const FOOTER_PADDING =
  "px-4 pt-[31px] pb-8 max-lg:[@media(max-height:740px)]:pt-4 max-lg:[@media(max-height:740px)]:pb-5";

/** The 16px medium label every button on these screens carries. */
const BUTTON_TYPE = "text-[16px] md:text-[16px] leading-[24px] font-medium";

/**
 * Back and Cancel are words under the button, not buttons. The design gives
 * the word a box as tall as its capitals, which is what keeps the footer at
 * the drawn height; the padding a finger needs is added around it without
 * taking up room.
 */
const TEXT_ACTION =
  "relative h-3 p-0 self-center before:absolute before:inset-x-0 before:-inset-y-3 before:content-['']";
const TEXT_ACTION_TYPE = "text-[16px] md:text-[16px] leading-none font-medium";

function HomeLink({
  href,
  theme,
}: {
  href: string;
  theme?: "light" | "dark";
}) {
  return (
    <a
      href={href}
      title="Home"
      aria-label="Pollinator home"
      className="with-ring focus:rounded-lg focus:ring-offset-[16px]"
      data-testid="intake-home"
    >
      <PollinatorLogo size="sm" theme={theme} />
    </a>
  );
}

/**
 * The shape every intake page takes: an argument on one side, a form on the
 * other, split 880 to 560 as the design splits it.
 *
 * The left panel is why somebody is here and does not change while they work,
 * which is the point of it: the reason stays visible through five steps of
 * answering. Its copy is configured per form rather than written here, because
 * each audience is being told something different.
 *
 * The step sits centred between the strip above it and the footer below, on
 * every step, which is how the design places even its longest form. A form is
 * one screen tall on every size: where a step cannot fit, it scrolls between
 * the strip and the footer, and the page itself never does, so the button
 * that moves on is always on screen. A short phone screen (740px or less)
 * closes up the bar, the strip, the footer and the gaps first.
 *
 * On a phone the design does not stack the two: the argument is a screen of
 * its own with a Continue button, and the form follows without it. A page
 * with no steps to walk through can still ask for both on one page.
 *
 * Nothing else is on these pages, so the way out is part of the layout: the
 * logo leads home from every screen, and where there is no step to go back to
 * the footer offers Cancel in the place Back would be. On a phone the first
 * form screen does have somewhere to go back to, the intro, so Cancel sits
 * under Continue there instead.
 */
export function MarketingIntakeLayout({
  audienceLabel,
  headline,
  introBody,
  stepIntro,
  stepNumber,
  stepCount,
  primaryLabel = "Continue",
  onBack,
  cancelHref,
  onMobileBack,
  homeHref = "/",
  onPrimary,
  isPrimaryDisabled,
  isPrimaryHidden,
  isSubmitting,
  secondaryAction,
  mobileView = "stacked",
  onMobileContinue,
  children,
  className,
}: Props) {
  // A form walked a screen at a time is exactly one screen tall, on every
  // size: what a screen holds scrolls inside its own panel if it must, and
  // the page itself never does, so the button that moves on is always on
  // screen. A page with both panels stacked is read top to bottom instead.
  const isPaged = mobileView !== "stacked";

  // The page is these two panels and nothing else, so they are its main
  // content: there is no header, navigation or footer around them.
  return (
    <main
      className={`w-full grid grid-cols-1 lg:grid-cols-[minmax(0,11fr)_minmax(0,7fr)] ${
        isPaged ? "h-[100dvh] grid-rows-[minmax(0,1fr)] overflow-hidden" : "min-h-screen"
      } ${className ?? ""}`}
      data-testid="intake-layout"
    >
      <div
        className={`relative bg-brand-800 text-brand-50 flex-col lg:flex lg:pl-12 lg:pr-6 lg:py-6 ${
          mobileView === "form" ? "hidden" : "flex"
        } ${isPaged ? "min-h-0" : ""}`}
      >
        {/* A bar across the top of a phone; out of the flow on a wide screen,
            so the argument centres on the panel rather than under the logo. */}
        <div
          className={`${MOBILE_BAR} lg:block lg:h-auto lg:p-0 lg:border-0 lg:absolute lg:left-12 lg:top-9`}
        >
          <HomeLink href={homeHref} theme="light" />
        </div>
        {/* The argument scrolls within its panel when it cannot fit, centred
            when it can. On a short window it stops lifting itself above centre
            and keeps clear of the logo instead. */}
        <div className={`flex flex-1 flex-col ${isPaged ? "min-h-0 overflow-y-auto" : ""}`}>
        <div className="my-auto flex flex-col gap-6 px-8 py-8 lg:px-0 lg:py-0 lg:pb-24 lg:max-w-[608px] lg:[@media(max-height:900px)]:pb-0 lg:[@media(max-height:900px)]:pt-14 lg:[@media(max-height:800px)]:gap-4 max-lg:[@media(max-height:740px)]:gap-4 max-lg:[@media(max-height:740px)]:py-5">
          {audienceLabel && (
            <Type
              variant="caption"
              as="p"
              className={`${marketingType.eyebrow} ${EYEBROW_ACCENT} self-start max-w-none`}
            >
              {audienceLabel}
            </Type>
          )}
          {headline && (
            <Type
              variant="h1"
              as="h1"
              // The phone headline is drawn in a box trimmed to its capitals,
              // so the gaps above and below are measured from the letters
              // rather than from the line. A browser without the property
              // leaves the line box whole and the headline a few pixels low.
              className="leading-[45px] tracking-[-0.72px] max-md:[text-box:trim-both_cap_alphabetic] md:text-[78px] md:leading-[97.5px] md:tracking-[-1.56px] max-lg:[@media(max-height:740px)]:text-[30px] max-lg:[@media(max-height:740px)]:leading-[37px]"
            >
              {headline}
            </Type>
          )}
          {(introBody ?? []).length > 0 && (
            <div className="flex flex-col gap-4">
              {(introBody ?? []).map((paragraph) => (
                <Type
                  variant="body1"
                  key={paragraph}
                  // A short phone screen sets the argument a size down, so it
                  // fits above its Continue rather than scrolling.
                  className="text-[20px] leading-[30px] md:text-[20px] md:leading-[30px] max-w-none max-lg:[@media(max-height:740px)]:text-[17px] max-lg:[@media(max-height:740px)]:leading-[25px]"
                >
                  {withEmailLinks(paragraph)}
                </Type>
              ))}
            </div>
          )}
        </div>
        </div>
        {mobileView === "intro" && onMobileContinue && (
          <div className={`flex shrink-0 flex-col gap-4 border-t border-brand-700 ${FOOTER_PADDING} lg:hidden`}>
            <Button
              theme="dark"
              onClick={onMobileContinue}
              className="w-full h-11 px-4"
              typeClassName={BUTTON_TYPE}
              data-testid="intake-mobile-continue"
            >
              Continue
            </Button>
            {cancelHref && (
              <Button
                as="a"
                href={cancelHref}
                variant="link"
                theme="dark"
                className={TEXT_ACTION}
                typeClassName={TEXT_ACTION_TYPE}
                data-testid="intake-mobile-cancel"
              >
                Cancel
              </Button>
            )}
          </div>
        )}
      </div>

      <div
        className={`bg-brand-50 text-brand-800 flex-col lg:flex ${
          mobileView === "intro" ? "hidden" : "flex"
        } ${isPaged ? "min-h-0" : ""}`}
      >
        {mobileView === "form" && (
          <div className={`${MOBILE_BAR} lg:hidden`}>
            <HomeLink href={homeHref} />
          </div>
        )}
        {/* A phone showing the form hides the panel the headline is in, which
            left the page with no heading at all for a screen reader to start
            from. The headline is kept for them here, unseen; a wide screen
            shows the panel and its own. */}
        {mobileView === "form" && headline && (
          <div className="lg:hidden">
            <Type variant="h1" as="h1" className="sr-only">
              {headline}
            </Type>
          </div>
        )}
        {stepIntro && (
          // The design rules this strip in the accent green, as it does the
          // footer, rather than in the dark green used for borders elsewhere.
          <div className="shrink-0 border-b border-brand-600 p-6 lg:p-8 max-lg:[@media(max-height:740px)]:px-6 max-lg:[@media(max-height:740px)]:py-4">
            <Type
              variant="body1"
              as="p"
              className={`${marketingType.body} font-medium max-w-none`}
            >
              {stepIntro}
            </Type>
          </div>
        )}

        {/* The step, centred between the strip and the footer when it fits,
            and scrolling between them when it does not. */}
        <div className={`flex flex-1 flex-col ${isPaged ? "min-h-0 overflow-y-auto" : ""}`} data-testid="intake-step-area">
          <div className="my-auto flex flex-col px-[26px] py-[42px] lg:px-8 lg:py-6 lg:[@media(max-height:900px)]:py-3 lg:[@media(max-height:800px)]:py-2 max-lg:[@media(max-height:740px)]:py-5">
            {children}
          </div>
        </div>

        {onPrimary && (
          <div
            className={`shrink-0 border-t border-brand-700 ${FOOTER_PADDING} lg:border-brand-600 lg:px-8 lg:[@media(max-height:900px)]:py-4 lg:[@media(max-height:800px)]:py-3 flex flex-col gap-4 max-lg:[@media(max-height:740px)]:gap-3`}
          >
            {stepCount && stepCount > 1 && (
              <Type variant="caption" as="p" className="text-center max-w-none">
                {stepNumber} of {stepCount}
              </Type>
            )}
            {!isPrimaryHidden && (
              <Button
                onClick={onPrimary}
                disabled={isSubmitting}
                visuallyDisabled={!isSubmitting && isPrimaryDisabled}
                isLoading={isSubmitting}
                className="h-11 px-4"
                typeClassName={BUTTON_TYPE}
                data-testid="intake-primary"
              >
                {primaryLabel}
              </Button>
            )}
            {secondaryAction}
            {onBack && (
              <Button
                variant="link"
                onClick={onBack}
                className={TEXT_ACTION}
                typeClassName={TEXT_ACTION_TYPE}
                data-testid="intake-back"
              >
                Back
              </Button>
            )}
            {!onBack && onMobileBack && (
              <Button
                variant="link"
                onClick={onMobileBack}
                className={`${TEXT_ACTION} lg:hidden`}
                typeClassName={TEXT_ACTION_TYPE}
                data-testid="intake-mobile-back"
              >
                Back
              </Button>
            )}
            {!onBack && cancelHref && (
              <Button
                as="a"
                href={cancelHref}
                variant="link"
                // A phone that came through the intro goes back to it, and
                // was offered Cancel there.
                className={`${TEXT_ACTION} ${onMobileBack ? "hidden lg:flex" : ""}`}
                typeClassName={TEXT_ACTION_TYPE}
                data-testid="intake-cancel"
              >
                Cancel
              </Button>
            )}
          </div>
        )}
      </div>
    </main>
  );
}

export default MarketingIntakeLayout;
